import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Save, Copy, Check, ExternalLink, MessageCircle, Video, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const buildMessage = (cfg, link) => {
  const base = (cfg.welcome_message || "").includes("{link}") ? cfg.welcome_message.replace("{link}", link) : `${cfg.welcome_message || ""}\n\n👉 ${link}`;
  const videos = (cfg.videos || []).map((v, i) => `🎬 Video ${i + 1} · ${v.title}\n${v.url}`).join("\n\n");
  return videos ? `${base.trim()}\n\n${videos}` : base.trim();
};

export const WelcomeLinkPanel = () => {
  const { authHeader } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState("");
  const link = `${window.location.origin}/bienvenida`;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/config/welcome`, { headers: authHeader() });
      setCfg(r.data);
    } catch { toast.error("No se pudo cargar el enlace de bienvenida"); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const copy = async (text, key, msg) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(msg);
      setTimeout(() => setCopied(""), 1800);
    } catch { toast.error("No pudimos copiar. Copia manualmente."); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/config/welcome`, {
        headline: cfg.headline, intro: cfg.intro, welcome_message: cfg.welcome_message, videos: cfg.videos,
      }, { headers: authHeader() });
      setCfg(r.data);
      toast.success("Enlace de bienvenida guardado");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" /></div>;

  const fullMessage = buildMessage(cfg, link);
  const setVideo = (i, patch) => setCfg({ ...cfg, videos: cfg.videos.map((v, j) => (j === i ? { ...v, ...patch } : v)) });

  return (
    <div className="space-y-5" data-testid="admin-welcome-panel">
      <div className="rounded-2xl border border-[#d4af37]/30 bg-gradient-to-r from-[#d4af37]/[0.06] to-transparent p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center shrink-0">
            <Video className="w-5 h-5 text-[#d4af37]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#d4af37]">Enlace de bienvenida para clientes</div>
            <a href={link} target="_blank" rel="noreferrer" data-testid="admin-welcome-link" className="text-sm text-white hover:text-[#d4af37] truncate block">{link}</a>
            <p className="text-[11px] text-neutral-400">Videos de presentación + pasos + métodos de pago + registro. Los videos se reproducen también desde el navegador de WhatsApp.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="admin-welcome-copy-link" onClick={() => copy(link, "link", "Enlace copiado")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#d4af37]/50 text-[#d4af37] text-[11px] uppercase tracking-widest hover:bg-[#d4af37]/10">
            {copied === "link" ? <Check className="w-3.5 h-3.5" /> : <LinkIcon className="w-3.5 h-3.5" />} Copiar enlace
          </button>
          <button data-testid="admin-welcome-copy-message" onClick={() => copy(fullMessage, "msg", "Mensaje + enlace copiados")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#d4af37] text-black font-bold text-[11px] uppercase tracking-widest hover:bg-[#e6c35a]">
            {copied === "msg" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} Copiar mensaje + enlace
          </button>
          <a data-testid="admin-welcome-send-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(fullMessage)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 text-[11px] uppercase tracking-widest hover:bg-emerald-500/25">
            <MessageCircle className="w-3.5 h-3.5" /> Enviar por WhatsApp
          </a>
          <a data-testid="admin-welcome-open" href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-neutral-300 text-[11px] uppercase tracking-widest hover:bg-white/5">
            <ExternalLink className="w-3.5 h-3.5" /> Ver página
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <div>
            <Label className="text-neutral-300 text-xs">Mensaje de bienvenida (usa {"{link}"} donde va el enlace)</Label>
            <Textarea data-testid="admin-welcome-message" rows={9} value={cfg.welcome_message} onChange={(e) => setCfg({ ...cfg, welcome_message: e.target.value })} className="bg-black/40 border-white/10 text-white text-sm" />
          </div>
          <div>
            <Label className="text-neutral-300 text-xs">Vista previa de lo que se copia / envía</Label>
            <pre data-testid="admin-welcome-preview" className="text-[11px] text-neutral-300 whitespace-pre-wrap rounded-xl border border-white/10 bg-black/40 p-3 max-h-56 overflow-y-auto">{fullMessage}</pre>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-neutral-300 text-xs">Título de la página</Label>
            <Input data-testid="admin-welcome-headline" value={cfg.headline} onChange={(e) => setCfg({ ...cfg, headline: e.target.value })} className="bg-black/40 border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-neutral-300 text-xs">Introducción</Label>
            <Textarea data-testid="admin-welcome-intro" rows={3} value={cfg.intro} onChange={(e) => setCfg({ ...cfg, intro: e.target.value })} className="bg-black/40 border-white/10 text-white text-sm" />
          </div>
          {cfg.videos.map((v, i) => (
            <div key={v.id} data-testid={`admin-welcome-video-${v.id}`} className="rounded-xl border border-white/10 bg-black/30 p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-[#d4af37]">Video {i + 1}</div>
              <Input data-testid={`admin-welcome-video-title-${v.id}`} value={v.title} onChange={(e) => setVideo(i, { title: e.target.value })} placeholder="Título" className="bg-black/40 border-white/10 text-white text-sm" />
              <Textarea data-testid={`admin-welcome-video-desc-${v.id}`} rows={3} value={v.description} onChange={(e) => setVideo(i, { description: e.target.value })} placeholder="Descripción" className="bg-black/40 border-white/10 text-white text-xs" />
              <Input data-testid={`admin-welcome-video-url-${v.id}`} value={v.url} onChange={(e) => setVideo(i, { url: e.target.value })} placeholder="URL del video (.mp4)" className="bg-black/40 border-white/10 text-neutral-300 text-xs" />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button data-testid="admin-welcome-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#d4af37] text-black font-bold text-[11px] uppercase tracking-widest hover:bg-[#e6c35a] disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar cambios
        </button>
      </div>
    </div>
  );
};

export default WelcomeLinkPanel;
